import React from 'react';
import { getMetaData, getSearchResult } from '../api/request';

export const SearchResultContext = React.createContext({
    results: null,
    setResults: () => null,
    metaData: null,
    setMetaData: () => null,
    activeCategory: '',
    setActiveCategory: () => null,
    endpoint: '',
    setEndpoint: () => null,
    page: 1,
    setPage: () => null,
    itemsPerPage: 12,
    setItemsPerPage: () => null,
    sortOrder: '',
    setSortOrder: () => null,
    language: '',
    setLanguage: () => null,
    query: ''
});

const SearchResultProvider = ({ children }) => {

    const queryParams = new URLSearchParams(window.location.search);
    const query = queryParams.get('q');
    const activeTab = queryParams.get('activeTab');
    const sortParam = queryParams.get('sort');

    const [results, setResults] = React.useState(null);
    const [endpoint, setEndpoint] = React.useState('');
    const [metaData, setMetaData] = React.useState(null);
    const [page, setPage] = React.useState(queryParams.get('page') || 1);
    const [itemsPerPage, setItemsPerPage] = React.useState(12);
    const [sortOrder, setSortOrder] = React.useState(sortParam || '');
    const [language, setLanguage] = React.useState('');

    const [activeCategory, setActiveCategory] = React.useState(activeTab === 'all' ? 'commerce' : activeTab || 'commerce');
    const [loading, setLoading] = React.useState(true);

    // Handle browser back/forward navigation
    React.useEffect(() => {
        const handlePopState = () => {
            const newQueryParams = new URLSearchParams(window.location.search);
            const newPage = newQueryParams.get('page') || 1;
            const newActiveTab = newQueryParams.get('activeTab');

            setPage(newPage);
            if (newActiveTab) {
                setActiveCategory(newActiveTab === 'all' ? 'commerce' : newActiveTab);
            }
        };

        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, []);

    React.useEffect(() => {

        queryParams.set('page', page);
        sortOrder ? queryParams.set('sort', sortOrder) : queryParams.delete('sort');

        const newUrl = `${window.location.pathname}?${queryParams.toString()}`;
        if (query !== null) window.history.pushState({}, '', newUrl);

        const fetchResults = async () => {
            setResults(null);
            setLoading(true);
            try {
                const params = await getSearchResult(language, endpoint, query, activeCategory, (page - 1) * itemsPerPage, itemsPerPage, sortOrder);
                setResults(params);
            } catch (error) {
                console.log(error);
                console.error('Error fetching data:', error);
            } finally {
                setLoading(false);
            }
        };

        if (language && query !== null) fetchResults();
    }, [activeCategory, endpoint, page, itemsPerPage, sortOrder, language]);

    React.useEffect(() => {

        queryParams.set('page', page);
        const newUrl = `${window.location.pathname}?${queryParams.toString()}`;
        if (query !== null) window.history.replaceState({}, '', newUrl);

        const fetchMetaData = async () => {
            setLoading(true);

            try {
                const params = await getMetaData(language, endpoint, query);
                setMetaData(params);
            } catch (error) {
                console.log(error);
                console.error('Error fetching data:', error);
            } finally {
                setLoading(false);
            }
        };

        if (language && query !== null) fetchMetaData();
    }, [endpoint, language]);

    return (
        <SearchResultContext.Provider value={{
            metaData,
            setMetaData,
            results,
            setResults,
            activeCategory,
            setActiveCategory,
            loading,
            setEndpoint,
            endpoint,
            page,
            setPage,
            itemsPerPage,
            setItemsPerPage,
            sortOrder,
            setSortOrder,
            setLanguage,
            language,
            query
        }}>
            {children}
        </SearchResultContext.Provider>
    );
};

export default SearchResultProvider;
